"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDown, ChevronSelectorVertical, Copy01, Edit01, HelpCircle, Trash01 } from "@untitledui/icons";
import type { ComponentPropsWithRef, HTMLAttributes, ReactNode, Ref, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { createContext, isValidElement, use } from "react";
import type { CellProps as AriaCellProps, ColumnProps as AriaColumnProps, RowProps as AriaRowProps, TableHeaderProps as AriaTableHeaderProps, TableProps as AriaTableProps } from "react-aria-components";
import { Cell as AriaCell, Collection as AriaCollection, Column as AriaColumn, Group as AriaGroup, Row as AriaRow, Table as AriaTable, TableBody as AriaTableBody, TableHeader as AriaTableHeader, useTableOptions } from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
export const TableRowActionsDropdown = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Dropdown.DotsButton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dropdown.Item icon={Edit01}><span className="pr-4">Edit</span></Dropdown.Item>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Dropdown.Item icon={Copy01}><span className="pr-4">Copy Link</span></Dropdown.Item>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Dropdown.Root>{t0}<Dropdown.Popover className="w-min"><Dropdown.Menu>{t1}{t2}<Dropdown.Item icon={Trash01}><span className="pr-4">Delete</span></Dropdown.Item></Dropdown.Menu></Dropdown.Popover></Dropdown.Root>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
const TableContext = createContext<{
  size: "sm" | "md";
}>({
  size: "md"
});
const TableCardRoot = t0 => {
  const $ = _c(16);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "md" : t1;
  let t2;
  if ($[5] !== size) {
    t2 = {
      size
    };
    $[5] = size;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className) {
    t3 = cx("overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t3) {
    t4 = <div {...props} className={t3}>{children}</div>;
    $[9] = children;
    $[10] = props;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t4) {
    t5 = <TableContext.Provider value={t2}>{t4}</TableContext.Provider>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
interface TableCardHeaderProps {
  /** The title of the table card header. */
  title: string;
  /** The badge displayed next to the title. */
  badge?: ReactNode;
  /** The description of the table card header. */
  description?: string;
  /** The content displayed after the title and badge. */
  contentTrailing?: ReactNode;
  /** The class name of the table card header. */
  className?: string;
}
const TableCardHeader = t0 => {
  const $ = _c(22);
  const {
    title,
    badge,
    description,
    contentTrailing,
    className
  } = t0;
  const {
    size
  } = use(TableContext);
  const t1 = size === "sm" ? "py-4 md:px-5" : "py-5 md:px-6";
  let t2;
  if ($[0] !== className || $[1] !== t1) {
    t2 = cx("relative flex flex-col items-start gap-4 border-b border-secondary bg-primary px-4 md:flex-row", t1, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = size === "sm" ? "text-md" : "text-lg";
  let t4;
  if ($[3] !== t3) {
    t4 = cx("font-semibold text-primary", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4 || $[6] !== title) {
    t5 = <h2 className={t4}>{title}</h2>;
    $[5] = t4;
    $[6] = title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== badge) {
    t6 = badge ? isValidElement(badge) ? badge : <Badge color="brand" size="sm">{badge}</Badge> : null;
    $[8] = badge;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div className="flex items-center gap-2">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== description) {
    t8 = description && <p className="text-sm text-tertiary">{description}</p>;
    $[13] = description;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="flex flex-1 flex-col gap-0.5">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== contentTrailing || $[19] !== t2 || $[20] !== t9) {
    t10 = <div className={t2}>{t9}{contentTrailing}</div>;
    $[18] = contentTrailing;
    $[19] = t2;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};
interface TableRootProps extends AriaTableProps, Omit<ComponentPropsWithRef<"table">, "className" | "slot" | "style"> {
  size?: "sm" | "md";
}
const TableRoot = t0 => {
  const $ = _c(14);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const size = t1 === undefined ? "md" : t1;
  const context = use(TableContext);
  const t2 = context?.size ?? size;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      size: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== className) {
    t4 = state => cx("w-full overflow-x-hidden", typeof className === "function" ? className(state) : className);
    $[6] = className;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== props || $[9] !== t4) {
    t5 = <div className="overflow-x-auto"><AriaTable className={t4} {...props} /></div>;
    $[8] = props;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <TableContext.Provider value={t3}>{t5}</TableContext.Provider>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
TableRoot.displayName = "Table";
interface TableHeaderProps<T extends object> extends AriaTableHeaderProps<T>, Omit<ComponentPropsWithRef<"thead">, "children" | "className" | "slot" | "style"> {
  bordered?: boolean;
}
const TableHeader = t0 => {
  const $ = _c(22);
  let children;
  let className;
  let columns;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      columns,
      children,
      bordered: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = columns;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    columns = $[3];
    props = $[4];
    t1 = $[5];
  }
  const bordered = t1 === undefined ? true : t1;
  const {
    size
  } = use(TableContext);
  const {
    selectionBehavior,
    selectionMode
  } = useTableOptions();
  let t2;
  if ($[6] !== bordered || $[7] !== className || $[8] !== size) {
    t2 = state => cx("relative bg-secondary", size === "sm" ? "h-9" : "h-11", bordered && "[&>tr>th]:after:pointer-events-none [&>tr>th]:after:absolute [&>tr>th]:after:inset-x-0 [&>tr>th]:after:bottom-0 [&>tr>th]:after:h-px [&>tr>th]:after:bg-border-secondary [&>tr>th]:focus-visible:after:bg-transparent", typeof className === "function" ? className(state) : className);
    $[6] = bordered;
    $[7] = className;
    $[8] = size;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== selectionBehavior || $[11] !== selectionMode || $[12] !== size) {
    t3 = selectionBehavior === "toggle" && <AriaColumn className={cx("relative py-2 pr-0 pl-4", size === "sm" ? "w-9 md:pl-5" : "w-11 md:pl-6")}>{selectionMode === "multiple" && <div className="flex items-start"><Checkbox slot="selection" size={size} /></div>}</AriaColumn>;
    $[10] = selectionBehavior;
    $[11] = selectionMode;
    $[12] = size;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== children || $[15] !== columns) {
    t4 = <AriaCollection items={columns}>{children}</AriaCollection>;
    $[14] = children;
    $[15] = columns;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== props || $[18] !== t2 || $[19] !== t3 || $[20] !== t4) {
    t5 = <AriaTableHeader {...props} className={t2}>{t3}{t4}</AriaTableHeader>;
    $[17] = props;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
TableHeader.displayName = "TableHeader";
interface TableHeadProps extends AriaColumnProps, Omit<ThHTMLAttributes<HTMLTableCellElement>, "children" | "className" | "style" | "id"> {
  label?: string;
  tooltip?: string;
}
const TableHead = t0 => {
  const $ = _c(17);
  let children;
  let className;
  let label;
  let props;
  let tooltip;
  if ($[0] !== t0) {
    ({
      className,
      tooltip,
      label,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = label;
    $[4] = props;
    $[5] = tooltip;
  } else {
    children = $[1];
    className = $[2];
    label = $[3];
    props = $[4];
    tooltip = $[5];
  }
  const {
    selectionBehavior
  } = useTableOptions();
  let t1;
  if ($[6] !== className || $[7] !== selectionBehavior) {
    t1 = state => cx("group/table-head relative p-0 px-6 py-2 outline-hidden focus-visible:z-1 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-bg-primary focus-visible:ring-inset", selectionBehavior === "toggle" && "nth-2:pl-3", state.allowsSorting && "cursor-pointer", typeof className === "function" ? className(state) : className);
    $[6] = className;
    $[7] = selectionBehavior;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== children || $[10] !== label || $[11] !== tooltip) {
    t2 = state_0 => <AriaGroup className="flex items-center gap-1"><div className="flex items-center gap-1">{label && <span className={cx("text-primary-900 text-xs font-bold whitespace-nowrap", state_0.allowsSorting && state_0.sortDirection && "font-extrabold text-fg-primary", state_0.allowsSorting && "group-hover/table-head:underline group-hover/table-head:underline-offset-3", state_0.sortDirection && "underline underline-offset-3")}>{label}</span>}{typeof children === "function" ? children(state_0) : children}</div>{tooltip && <Tooltip title={tooltip} placement="top"><TooltipTrigger aria-label={`${label} info`} className="cursor-pointer text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"><HelpCircle className="size-4" /></TooltipTrigger></Tooltip>}{state_0.allowsSorting && (state_0.sortDirection ? <ArrowDown className={cx("size-4 stroke-[4px] font-bold text-fg-primary", state_0.sortDirection === "ascending" && "rotate-180")} /> : <ChevronSelectorVertical size={12} strokeWidth={3} className="text-fg-quaternary" />)}</AriaGroup>;
    $[9] = children;
    $[10] = label;
    $[11] = tooltip;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== props || $[14] !== t1 || $[15] !== t2) {
    t3 = <AriaColumn {...props} className={t1}>{t2}</AriaColumn>;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
};
TableHead.displayName = "TableHead";
interface TableRowProps<T extends object> extends AriaRowProps<T>, Omit<ComponentPropsWithRef<"tr">, "children" | "className" | "onClick" | "slot" | "style" | "id"> {
  highlightSelectedRow?: boolean;
}
const TableRow = t0 => {
  const $ = _c(21);
  let children;
  let className;
  let columns;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      columns,
      children,
      className,
      highlightSelectedRow: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = columns;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    columns = $[3];
    props = $[4];
    t1 = $[5];
  }
  const highlightSelectedRow = t1 === undefined ? true : t1;
  const {
    size
  } = use(TableContext);
  const {
    selectionBehavior
  } = useTableOptions();
  let t2;
  if ($[6] !== className || $[7] !== highlightSelectedRow || $[8] !== size) {
    t2 = state => cx("relative outline-focus-ring transition-colors after:pointer-events-none hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2", size === "sm" ? "h-14" : "h-18", highlightSelectedRow && "selected:bg-secondary", "[&>td]:after:absolute [&>td]:after:inset-x-0 [&>td]:after:bottom-0 [&>td]:after:h-px [&>td]:after:w-full [&>td]:after:bg-border-secondary last:[&>td]:after:hidden focus-visible:[&>td]:after:opacity-0", typeof className === "function" ? className(state) : className);
    $[6] = className;
    $[7] = highlightSelectedRow;
    $[8] = size;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== selectionBehavior || $[11] !== size) {
    t3 = selectionBehavior === "toggle" && <AriaCell className={cx("relative py-2 pr-0 pl-4", size === "sm" ? "md:pl-5" : "md:pl-6")}><div className="flex items-end"><Checkbox slot="selection" size={size} /></div></AriaCell>;
    $[10] = selectionBehavior;
    $[11] = size;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== children || $[14] !== columns) {
    t4 = <AriaCollection items={columns}>{children}</AriaCollection>;
    $[13] = children;
    $[14] = columns;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== props || $[17] !== t2 || $[18] !== t3 || $[19] !== t4) {
    t5 = <AriaRow {...props} className={t2}>{t3}{t4}</AriaRow>;
    $[16] = props;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
};
TableRow.displayName = "TableRow";
interface TableCellProps extends AriaCellProps, Omit<TdHTMLAttributes<HTMLTableCellElement>, "children" | "className" | "style" | "id"> {
  ref?: Ref<HTMLTableCellElement>;
}
const TableCell = t0 => {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    size
  } = use(TableContext);
  const {
    selectionBehavior
  } = useTableOptions();
  let t1;
  if ($[4] !== className || $[5] !== selectionBehavior || $[6] !== size) {
    t1 = state => cx("relative text-sm font-normal text-tertiary outline-focus-ring focus-visible:z-1 focus-visible:outline-2 focus-visible:-outline-offset-2", size === "sm" && "px-5 py-3", size === "md" && "px-6 py-4", selectionBehavior === "toggle" && "nth-2:pl-3", typeof className === "function" ? className(state) : className);
    $[4] = className;
    $[5] = selectionBehavior;
    $[6] = size;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== children || $[9] !== props || $[10] !== t1) {
    t2 = <AriaCell {...props} className={t1}>{children}</AriaCell>;
    $[8] = children;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
TableCell.displayName = "TableCell";
const TableCard = {
  Root: TableCardRoot,
  Header: TableCardHeader
};
const Table = TableRoot as typeof TableRoot & {
  Body: typeof AriaTableBody;
  Cell: typeof TableCell;
  Head: typeof TableHead;
  Header: typeof TableHeader;
  Row: typeof TableRow;
};
Table.Body = AriaTableBody;
Table.Cell = TableCell;
Table.Head = TableHead;
Table.Header = TableHeader;
Table.Row = TableRow;
export { Table, TableCard };